import { lazy, Suspense, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

import { DashboardCard } from "./dashboard-card";
import { CompletenessPanel } from "./completeness-panel";
import { AgeBands, HowFound, Insurance, Referrers, Registrations } from "./proportion-charts";
import {
	ActiveByMonth,
	AiCard,
	BirthdaysCard,
	FinanceByMonth,
	FunnelCard,
	RecencyCard,
	TemperatureCard,
} from "./pulse-charts";
import { TotalsRow } from "./totals-row";
import { WhatsappHeatmap } from "./whatsapp-heatmap";

const GeographicMap = lazy(() =>
	import("./geographic-map").then((module) => ({ default: module.GeographicMap })),
);

export function DashboardPage() {
	return (
		<ErrorBoundary fallback={<DashboardError />}>
			<Suspense
				fallback={
					<div data-component="dashboard-page" data-state="loading">
						<DashboardSkeleton />
					</div>
				}
			>
				<DashboardData />
			</Suspense>
		</ErrorBoundary>
	);
}

type DashboardPanelKey = "operation" | "people" | "data";

function DashboardNavigation({
	activePanel,
	onSelect,
}: {
	activePanel: DashboardPanelKey;
	onSelect: (panel: DashboardPanelKey) => void;
}) {
	const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const tabs: { key: DashboardPanelKey; label: string }[] = [
		{ key: "operation", label: "operação" },
		{ key: "people", label: "pessoas" },
		{ key: "data", label: "dados" },
	];

	return (
		<div
			role="tablist"
			aria-label="áreas do dashboard"
			className="sticky top-14 z-20 -mx-4 mb-6 flex gap-4 border-b border-border bg-background/95 px-4 backdrop-blur sm:-mx-5 sm:px-5"
		>
			{tabs.map((tab, index) => {
				const active = tab.key === activePanel;

				return (
					<button
						key={tab.key}
						type="button"
						role="tab"
						aria-selected={active}
						aria-controls={`dashboard-panel-${tab.key}`}
						tabIndex={active ? 0 : -1}
						ref={(element) => {
							tabRefs.current[index] = element;
						}}
						className={cn(
							"workspace-tab min-w-20 justify-center capitalize sm:min-w-24",
							active
								? "border-primary text-primary"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
						onClick={() => onSelect(tab.key)}
						onKeyDown={(event) => {
							let nextIndex: number | null = null;

							if (event.key === "ArrowRight") {
								nextIndex = (index + 1) % tabs.length;
							}

							if (event.key === "ArrowLeft") {
								nextIndex = (index - 1 + tabs.length) % tabs.length;
							}

							if (event.key === "Home") {
								nextIndex = 0;
							}

							if (event.key === "End") {
								nextIndex = tabs.length - 1;
							}

							if (nextIndex !== null) {
								event.preventDefault();
								onSelect(tabs[nextIndex].key);
								tabRefs.current[nextIndex]?.focus();
							}
						}}
					>
						{tab.label}
					</button>
				);
			})}
		</div>
	);
}

function DashboardError() {
	return (
		<div data-component="dashboard-page" data-state="error">
			<p className="text-sm text-destructive">{DashboardLabels.error}</p>
		</div>
	);
}

function DashboardSection({
	id,
	title,
	children,
}: {
	id: string;
	title: string;
	children: ReactNode;
}) {
	return (
		<section id={id} className="scroll-mt-24 flex flex-col gap-4">
			<h2 className="type-section-title text-foreground">{title}</h2>

			{children}
		</section>
	);
}

function DashboardPanel({
	id,
	label,
	active,
	children,
}: {
	id: string;
	label: string;
	active: boolean;
	children: ReactNode;
}) {
	return (
		<section
			id={id}
			role="tabpanel"
			aria-label={label}
			aria-hidden={!active}
			hidden={!active}
			className={cn("scroll-mt-24", !active && "hidden")}
		>
			{children}
		</section>
	);
}

function DashboardData() {
	const [activePanel, setActivePanel] = useState<DashboardPanelKey>("operation");
	const [{ data: overview }, { data: pulse }] = [
		useSuspenseQuery(orpc.dashboard.overview.queryOptions()),
		useSuspenseQuery(orpc.dashboard.pulse.queryOptions()),
	];

	const activeMonth = pulse.active_by_month.at(-1)?.total ?? 0;

	return (
		<div data-component="dashboard-page" data-state="ready" className="flex flex-col gap-8">
			<DashboardNavigation activePanel={activePanel} onSelect={setActivePanel} />

			<DashboardPanel
				id="dashboard-panel-operation"
				label="operação"
				active={activePanel === "operation"}
			>
				<div className="flex flex-col gap-8">
					<div id="dashboard-overview" data-slot="totals" className="scroll-mt-24">
						<TotalsRow
							totals={overview.totals}
							activeMonth={activeMonth}
							response={pulse.response}
						/>
					</div>

					<DashboardSection id="dashboard-commercial" title={DashboardLabels.sections.commercial}>
						<div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.6fr_1fr]">
							<FunnelCard items={pulse.funnel} />
							<TemperatureCard items={pulse.temperature} />
						</div>
					</DashboardSection>

					<DashboardSection id="dashboard-growth" title={DashboardLabels.sections.growth}>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
							<Registrations months={overview.registrations_by_month} />
							<ActiveByMonth months={pulse.active_by_month} />
							<RecencyCard items={pulse.recency} />
						</div>

						<FinanceByMonth months={pulse.finance_by_month} />
					</DashboardSection>

					<div id="dashboard-activity" className="scroll-mt-24">
						<WhatsappHeatmap cells={pulse.heatmap} />
					</div>
				</div>
			</DashboardPanel>

			<DashboardPanel id="dashboard-panel-people" label="pessoas" active={activePanel === "people"}>
				<div className="flex flex-col gap-8">
					<div
						id="dashboard-location"
						className="scroll-mt-24 grid grid-cols-1 gap-4 xl:grid-cols-[1.7fr_1fr]"
					>
						<div data-slot="map">
							<Suspense fallback={<Skeleton className="h-105" />}>
								<GeographicMap cities={overview.cities} active={activePanel === "people"} />
							</Suspense>
						</div>

						<Referrers items={overview.referrers} />
					</div>

					<DashboardSection id="dashboard-profile" title={DashboardLabels.sections.profile}>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
							<div data-slot="age-bands">
								<AgeBands bands={overview.age_bands} age={overview.age} />
							</div>
							<Insurance items={overview.insurance} />
							<HowFound items={overview.how_found} />
						</div>
					</DashboardSection>

					<BirthdaysCard items={pulse.birthdays} />
				</div>
			</DashboardPanel>

			<DashboardPanel id="dashboard-panel-data" label="dados" active={activePanel === "data"}>
				<div className="flex flex-col gap-8">
					<div data-slot="completeness">
						<CompletenessPanel completeness={overview.completeness} />
					</div>

					<AiCard ai={pulse.ai} />
				</div>
			</DashboardPanel>
		</div>
	);
}

function DashboardSkeleton() {
	return (
		<div data-component="dashboard-skeleton" className="flex flex-col gap-6">
			<div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
				{Array.from({ length: 4 }, (_, i) => (
					<Skeleton key={i} className="h-18" />
				))}
			</div>

			<DashboardCard className="p-4">
				<Skeleton className="h-55" />
			</DashboardCard>

			<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
				{Array.from({ length: 2 }, (_, i) => (
					<DashboardCard key={i} className="p-4">
						<Skeleton className="h-55" />
					</DashboardCard>
				))}
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
				{Array.from({ length: 3 }, (_, i) => (
					<DashboardCard key={i} className="p-4">
						<Skeleton className="h-55" />
					</DashboardCard>
				))}
			</div>

			<DashboardCard className="p-4">
				<Skeleton className="h-105" />
			</DashboardCard>
		</div>
	);
}
